import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Coffee, Book, Film } from "lucide-react";

const donationTiers = [
  {
    id: "supporter",
    name: "Supporter",
    amount: 25,
    icon: <Coffee className="h-8 w-8 text-amber-500" />,
    description: "Help us create educational content",
    benefits: [
      "Thank you email from the team",
      "Updates on new episodes and activities",
      "Join our community newsletter"
    ],
    gradient: "from-amber-500/20 to-orange-500/20"
  },
  {
    id: "advocate",
    name: "Advocate", 
    amount: 100,
    icon: <Book className="h-8 w-8 text-blue-500" />,
    description: "Support our educational mission",
    benefits: [
      "All Supporter benefits",
      "Exclusive behind-the-scenes content",
      "Early access to new episodes",
      "Digital certificate of appreciation"
    ],
    popular: true,
    gradient: "from-blue-500/20 to-purple-500/20"
  },
  {
    id: "champion",
    name: "Champion",
    amount: 250,
    icon: <Film className="h-8 w-8 text-green-500" />,
    description: "Help us reach more families",
    benefits: [
      "All Advocate benefits",
      "Name in episode credits (optional)",
      "Quarterly impact reports",
      "Invitation to virtual events",
      "Priority customer support"
    ],
    gradient: "from-green-500/20 to-teal-500/20"
  }
];

export const DonationOptions = () => {
  const scrollToForm = (amount?: number) => {
    const form = document.getElementById('donation-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth' });
      // Pre-fill amount in form
      if (amount) {
        setTimeout(() => {
          const amountInput = document.getElementById('donation-amount') as HTMLInputElement;
          if (amountInput) {
            amountInput.value = amount.toString();
            amountInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }, 500);
      }
    }
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Donation Levels
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose a donation level that works for you. Every contribution helps us create 
            more educational content and reach more children around the world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {donationTiers.map((tier) => {
            const isPopular = tier.popular;
            
            return (
              <Card 
                key={tier.id} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isPopular ? 'ring-2 ring-primary shadow-lg' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      <Star className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <div className={`absolute inset-0 bg-gradient-to-br ${tier.gradient} opacity-50`} />
                
                <CardHeader className="relative text-center pb-4">
                  <div className="flex justify-center mb-4">
                    {tier.icon}
                  </div>
                  <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                  <div className="text-3xl font-bold text-primary">
                    KYD ${tier.amount}
                  </div>
                  <p className="text-muted-foreground mt-2">{tier.description}</p>
                </CardHeader>

                <CardContent className="relative">
                  <ul className="space-y-3 mb-8">
                    {tier.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={() => scrollToForm(tier.amount)}
                    className={`w-full ${isPopular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={isPopular ? "default" : "outline"}
                  >
                    Donate ${tier.amount}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Want to donate a different amount?{" "}
          </p>
          <Button 
            onClick={() => scrollToForm()}
            variant="outline"
            className="font-semibold"
          >
            Choose Your Own Amount
          </Button>
        </div>
      </div>
    </section>
  );
};